import React, { FC, useEffect, useMemo } from 'react';
import { Form } from 'react-final-form';
import UnavailableDateFields from 'src/calendar/components/UnavailableDateFields';
import UnavailableEditorSection from 'src/calendar/components/UnavailableEditorSection';
import UnavailableTimeFields from 'src/calendar/components/UnavailableTimeFields';
import { AdHocEventFormState } from 'src/calendar/types';
import {
  adHocEventInitialValues,
  validateAdHocEvent,
} from 'src/calendar/utils/unavailableEditorUtils';
import UniversalLink from 'src/common/components/UniversalLink';
import { PATHS } from 'src/common/constants';
import type { FullBoatDetail } from 'src/types/boat/BoatDetail';
import { formatDate } from 'src/common/utils/dateHelpers';
import { trackEvent } from 'src/common/tracking';
import FormButtons from '../FormButtons';
import FormError from '../FormError';
import Icon from '../IconDS22';
import Modal from '../Modal';
import GA_EVENTS from './constants';
import s from './UpdateAvailability.module.scss';
import UpdateAvailabilityHeader from './UpdateAvailabilityHeader';

const today = formatDate(new Date(), 'yyyy-MM-dd');

type BlockTimeEditorModalProps = {
  closeModal: () => void;
  boatId: FullBoatDetail['boat_id'];
  boat: FullBoatDetail;
  formattedStartDate: string;
  formattedEndDate: string;
  formattedStartTime?: string;
  formattedEndTime?: string;
  isOpen: boolean;
  onSave: (editorData: AdHocEventFormState) => Promise<Response | void>;
};

const UpdateAvailabilityEditor: FC<BlockTimeEditorModalProps> = ({
  closeModal,
  boatId,
  boat,
  formattedStartDate: startDate,
  formattedEndDate: endDate,
  isOpen,
  formattedStartTime: startTime,
  formattedEndTime: endTime,
  onSave,
}) => {
  const initialValues = useMemo(
    () => adHocEventInitialValues(
      {
        boat_ids: [boatId],
        event_date: startDate,
        event_end_date: endDate,
        event_start_time: startTime,
        event_end_time: endTime,
        all_day: false,
      },
      [boat],
      today,
      startDate,
      [],
    ),
    [boatId, startDate, endDate, startTime, endTime, boat],
  );

  useEffect(() => {
    if (isOpen) {
      trackEvent(GA_EVENTS.UNAVAILABLE_EDITOR_MODAL.OPEN);
    }
  }, [isOpen]);

  return (
    <Modal
      modalOpen={isOpen}
      closeModal={closeModal}
      label="Calendar Update"
      name="block-time-editor-modal"
      disableOverlayClick
      classNameModifier="calendar large unavailableEditor"
    >
      <UpdateAvailabilityHeader
        title="Calendar Update"
        description={(
          <>
            Prevent booking inquiries to <strong>{boat.shortname}</strong> for this period.
            {' '}
            <UniversalLink to={PATHS.CALENDAR}>
              View calendar.
            </UniversalLink>
          </>
        )}
      />
      <Form<AdHocEventFormState>
        keepDirtyOnReinitialize
        initialValues={initialValues}
        onSubmit={onSave}
        validate={validateAdHocEvent}
      >
        {({ handleSubmit, errors, submitFailed }) => {
          const formError = (submitFailed && errors ? errors.formError : '');

          return (
            <form
              method="POST"
              noValidate
              className={s.form}
              onSubmit={handleSubmit}
            >
              <div className={s.controlsWrapper}>
                <UnavailableEditorSection
                  title="Blocked Time"
                  description="Blocked period for selected date."
                  classNameModifier="time"
                >
                  <UnavailableTimeFields />
                </UnavailableEditorSection>

                <UnavailableEditorSection title="Date">
                  <UnavailableDateFields />
                </UnavailableEditorSection>
              </div>

              <div className={s.buttonsWrapper}>
                <FormError
                  error={formError && (
                    <>
                      <Icon id="info" />
                      <span>{formError}</span>
                    </>
                  )}
                />
                <FormButtons
                  classNameModifier="inbox"
                  closeModal={closeModal}
                  submitText="Save"
                />
              </div>
            </form>
          );
        }}
      </Form>
    </Modal>
  );
};

export default UpdateAvailabilityEditor;
